//src/services/api.js
import axios from 'axios';

// Detect if accessing from network IP and adjust API URL
const getBaseURL = () => {
  const hostname = window.location.hostname;

  // Localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  }

  // Local network testing (mobile on LAN) — private IP ranges only
  if (/^(192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(hostname)) {
    return `http://${hostname}:3001/api`;
  }

  // Production (same origin, no port, matches page protocol)
  return `${window.location.protocol}//${hostname}/api`;
};

// Create base axios instance
const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Track refresh token promise for concurrent requests
let isRefreshing = false;
let refreshSubscribers = [];

// Helper function to add callbacks to be executed after token refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Helper function to execute callbacks after token refresh
const onRefreshed = (token) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle token expiration
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // If we're already refreshing, wait for the token and retry with the new token
      if (isRefreshing) {
        try {
          return new Promise(resolve => {
            subscribeTokenRefresh(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axios(originalRequest));
            });
          });
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Try to refresh the token
        const res = await axios.post(
          '/api/auth/refresh-token', 
          {}, 
          { withCredentials: true }
        );
        
        if (res.status === 200) {
          localStorage.setItem('authToken', res.data.token);
          api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
          onRefreshed(res.data.token);
          isRefreshing = false;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // If token refresh fails, redirect to login
        localStorage.removeItem('authToken');
        isRefreshing = false;
        
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
          // Dispatch a custom event to notify the app about authentication failure
          window.dispatchEvent(new CustomEvent('authError', {
            detail: {
              message: 'Your session has expired. Please log in again.'
            }
          }));
          
          // Redirect to home/login page
          window.location.href = '/';
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // ------------------------------------------------------------------
    // 402 — SUBSCRIPTION LAPSED
    // ------------------------------------------------------------------
    //
    // The server refuses writes from a company whose subscription has ended
    // (server/middleware/requireEntitlement.js). Rewriting the message HERE is
    // what makes that legible across the whole app without touching thirty
    // components: every screen already does
    //
    //     catch (err) { setError(err.message || 'Failed to save'); }
    //
    // so replacing err.message means the canvas, the millwork rail, the
    // hardware tab, cabinet and item order modals, Installation, Freight and
    // Management all display the right explanation the moment this ships —
    // including surfaces nobody has audited.
    //
    // It will not be beautiful everywhere. Some components render errors in
    // an awkward spot. But it is correct and comprehensible everywhere
    // immediately, and specific surfaces can be improved later as they are
    // noticed rather than as an up-front audit.
    //
    // DELIBERATELY NO REDIRECT, unlike the 401 path above. A 401 means the
    // session is gone and there is nothing to stay for. A 402 means the person
    // is looking at their own work and merely cannot change it — yanking them
    // to /pricing mid-edit would be hostile, and would lose whatever is on
    // screen. They get told, and they choose.
    if (error.response && error.response.status === 402) {
      const data = error.response.data || {};

      // Copy comes from entitlementService on the server, so the API response
      // and the in-app banner cannot describe the same situation differently.
      error.message = data.message
        || 'Your subscription has ended. Resubscribe to make changes.';

      // Structured flags for components that want to branch rather than just
      // print — e.g. swapping a Save button for a Resubscribe link.
      error.isSubscriptionLocked = true;
      error.entitlement = data.entitlement || 'locked';
      error.resubscribeUrl = data.resubscribeUrl || '/pricing';

      // Same CustomEvent pattern as the authError dispatch above. Lets the
      // banner appear on the first refused write without every component
      // having to report upward.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('subscriptionLocked', {
          detail: {
            message: error.message,
            entitlement: error.entitlement,
            resubscribeUrl: error.resubscribeUrl,
          }
        }));
      }
    }

    // Network errors
    if (!error.response) {
      error.message = 'Network error. Please check your connection.';
    }
    
    // Server errors
    if (error.response && error.response.status >= 500) {
      error.message = 'Server error. Please try again later.';
    }
    
    return Promise.reject(error);
  }
);

export default api;